import { gql } from "@apollo/client";
import Field from "controls/Field";
import FormDialog from "controls/FormDialog";
import FormSection from "controls/FormSection";
import Message from "controls/Message";
import { formatInTimeZone } from "date-fns-tz";
import noop from "helpers/noop";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import useTimeZone from "hooks/useTimeZone";
import SwitchInput from "inputs/SwitchInput";
import { Camera } from "mdi-material-ui";
import React, { useState } from "react";

import GameCutForm, { GameCutFormFragment } from "./GameCutForm";

CutNewFootageDialog.buttonProps = {
  icon: <Camera />,
  content: "Cut New Footage",
};

export default function CutNewFootageDialog({ gameId, onSubmit = noop, ...others }) {
  const [data] = useData(
    gql`
      query CutNewFootageDialog($gameId: ID!) {
        game(id: $gameId) {
          id
          playedAt
          completedAt
          court {
            id
            name
          }
          ...GameIssuesFragment
          ...GameCutFormFragment
        }
      }
      ${GameIssuesFragment}
      ${GameCutFormFragment}
    `,
    {
      gameId,
    },
  );

  const [recordingsAvailable, recordingsAvailableSet] = useState(false);
  const gameCutNewFootage = useActionFragment("gameCutNewFootage", "game { id video { id } }");

  return (
    <FormDialog
      header="Cut new video footage"
      loading={!data}
      disabled={!recordingsAvailable}
      onSubmit={async (formData) => {
        await gameCutNewFootage({
          input: {
            gameId,
            ...formData,
          },
        });
        await onSubmit();
      }}
      submitContent="Cut new video footage"
      {...others}
    >
      <GameIssues game={data?.game} />
      <Message
        type="warning"
        content={
          "This game’s details will be changed and a new video will be processed. Any existing video will be replaced."
        }
      />
      <GameCutForm game={data?.game} onRecordingsAvailableChange={recordingsAvailableSet} />
      <Field
        name="retimeApiEvents"
        label="Retime API events"
        defaultValue={false}
        input={<SwitchInput />}
        helperText="Tick when the scoring device clock was wrong. Set Start time to the exact moment the game starts: every scored event moves by the difference between the old and new start times, so a start time that is off moves every event off by the same amount."
      />
    </FormDialog>
  );
}

const GameIssuesFragment = gql`
  fragment GameIssuesFragment on Game {
    id
    isRecorded
    isFullRecordingAvailable
    isValidDuration
    durationError
    cameraOutages {
      id
      startedAt
      finishedAt
      active
    }
    serverOutages {
      id
      startedAt
      finishedAt
      active
    }
  }
`;

function GameIssues({ game }) {
  const issues = [];
  if (!game?.isRecorded) {
    issues.push(["No recording is available", `Unfortunately there was no recording found for this game`, "error"]);
  }
  if (game?.isRecorded && !game?.isFullRecordingAvailable) {
    issues.push(["Partial recording", "Unfortunately only part of this game was recorded", "error"]);
  }
  if (!game?.isValidDuration) {
    issues.push([
      "Suspicious game details",
      `This game had some unexpected details - ${game?.durationError}`,
      "warning",
    ]);
  }
  if (game?.cameraOutages?.length > 0) {
    issues.push(["Camera Outage", createOutageMessage(game.cameraOutages, "camera"), "error"]);
  }
  if (game?.serverOutages?.length > 0) {
    issues.push(["Server Outage", createOutageMessage(game.serverOutages, "venue server"), "error"]);
  }
  return (
    <FormSection header="Issues" defaultExpanded={true}>
      {issues?.map(([title, content, type], i) => (
        <Message key={i} title={title} content={content} type={type} />
      ))}
      {!issues.length && <Message content="No issues found" />}
    </FormSection>
  );
}

function createOutageMessage(outages, type) {
  let message = "";
  const datetimeFormat = "HH:mm dd-MM-yyyy";
  if (outages.length > 1) {
    message = `There were multiple outages. See ${type} for more info`;
  } else {
    const outage = outages[0];
    message = outage.active
      ? `Currently offline since ${formatInTimeZone(
          outage.startedAt,
          useTimeZone(),
          datetimeFormat,
        )} (${useTimeZone()})`
      : `Offline from ${formatInTimeZone(outage.startedAt, useTimeZone(), datetimeFormat)} to ${formatInTimeZone(
          outage.finishedAt,
          useTimeZone(),
          datetimeFormat,
        )} (${useTimeZone()})`;
  }
  return message;
}
